import React, { useRef, useState, useEffect, useCallback } from 'react'
import Modal from '@components/common/modal/Modal'
import FlexContainer from '@components/common/FlexContainer'
import TooltipModal from '@components/common/modal/TooltipModal'
import YSpacing from '@components/common/YSpacing'
import Table from '@components/common/Table'
import Loader from '@components/common/Loader'
import { Button, Checkbox, TextInput } from '@components/common/form'
import AuthorizedDisplay from '@containers/common/auth/AuthorizedDisplay'
import { Label } from '@res/styledComponents/index'
import { HubspotCompany, FullHubspotCompany } from '@types'

interface SearchViaHubSpotModalProps {
  close: () => void
  confirmationModal: { show: (val: { text: string }) => Promise<boolean> }
  getHubspotCompanyById: (id: string) => Promise<FullHubspotCompany | null>
  newAccountFromHubspot: (c: HubspotCompany, importContacts: boolean) => void
  newManualAccount: () => void
}

const SearchViaHubSpotModal = ({
  close,
  confirmationModal,
  getHubspotCompanyById,
  newAccountFromHubspot,
  newManualAccount,
}: SearchViaHubSpotModalProps) => {
  const [inputValue, setInputValue] = useState('')
  const [importContacts, setImportContacts] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCompany, setSelectedCompany] =
    useState<FullHubspotCompany | null>(null)
  const timer = useRef<NodeJS.Timeout | null>(null)

  const callAfterTimeout = useCallback(
    (func: () => void, delay = 1500) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
      timer.current = setTimeout(func, delay)
    },
    [timer],
  )

  const searchHubspotCompany = useCallback(async () => {
    setIsLoading(true)
    const company = await getHubspotCompanyById(inputValue)
    if (company) {
      setSelectedCompany(company)
    }
    setIsLoading(false)
  }, [inputValue, getHubspotCompanyById])

  useEffect(() => {
    callAfterTimeout(searchHubspotCompany, 1500)

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [inputValue, searchHubspotCompany, callAfterTimeout, timer])

  const onChangeInput = (e: InputEvent) => {
    setInputValue((e.target as HTMLInputElement).value)
  }

  const onManualInput = async () => {
    const confirmManual = await confirmationModal.show({
      text: 'Are you sure you wish to enter a client manually? Tech team suggests importing clients from Hubspot.',
    })
    if (confirmManual) {
      newManualAccount()
    }
  }

  const onClientSelect = async (company: HubspotCompany) => {
    const confirmCorrectClient = await confirmationModal.show({
      text: `Are you sure you want to create a client for ${company.name}?`,
    })
    if (selectedCompany && confirmCorrectClient) {
      newAccountFromHubspot(company, importContacts)
    }
  }

  return (
    <Modal width="1200px" title="Search Hubspot Clients" hideModal={close}>
      <FlexContainer flexDirection="row" alignItems="flex-start">
        <FlexContainer flexDirection="column" width="30%">
          <FlexContainer>
            <Label>Enter HubSpot Company Record Number</Label>
            <TooltipModal
              unicode="&#9432;"
              width="400px"
              information="Please ensure that you have successfully created a client in hubspot & then search by company's record number."
              marginTop="18px"
            />
          </FlexContainer>
          <TextInput
            type="text"
            placeholder="Record Number"
            value={inputValue}
            onChange={onChangeInput}
            width="200px"
          />
        </FlexContainer>
      </FlexContainer>
      <YSpacing height="10px" />
      <FlexContainer flexDirection="row">
        <Checkbox
          label="Import Contacts"
          checked={importContacts}
          onChange={() => setImportContacts(!importContacts)}
        />
      </FlexContainer>
      <YSpacing height="10px" />
      <FlexContainer flexDirection="column">
        {isLoading ? (
          <Loader />
        ) : (
          <Table
            headings={[
              'Name',
              'Domain',
              'Phone',
              'City/State',
              'Industry',
              'Contacts',
            ]}
          >
            {selectedCompany && (
              <tr
                key={selectedCompany.id}
                onClick={() => onClientSelect(selectedCompany)}
                style={{ cursor: 'pointer' }}
              >
                <td>{selectedCompany.name}</td>
                <td>{selectedCompany.domain}</td>
                <td>{selectedCompany.phone}</td>
                <td>
                  {selectedCompany.city}/{selectedCompany.state}
                </td>
                <td>{selectedCompany.industry}</td>
                <td>
                  {selectedCompany.contacts.length === 0
                    ? 'No Contacts'
                    : selectedCompany.contacts
                        .slice(0, 10)
                        .map(
                          (contact) =>
                            `${contact.firstName} ${contact.lastName} (${contact.email})`,
                        )
                        .join(', ') +
                      (selectedCompany.contacts.length > 10
                        ? `, and ${selectedCompany.contacts.length - 10} more`
                        : '')}
                </td>
              </tr>
            )}
          </Table>
        )}
      </FlexContainer>
      <YSpacing height="30px" />

      <AuthorizedDisplay roles={['master admin']}>
        <YSpacing height="10px" />
        <FlexContainer
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <p style={{ color: 'red' }}>
            Only master admins can manually create clients.
          </p>
          <YSpacing height="10px" />
          <Button label="Manual Entry" onClick={onManualInput} />
        </FlexContainer>
      </AuthorizedDisplay>
    </Modal>
  )
}

export default SearchViaHubSpotModal
